import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { IoClose } from "react-icons/io5";
import { IoDownload, IoFolder, IoDocument } from "react-icons/io5";

const TestCases = ({ isOpen, onClose }) => {
    const [testcases, setTestcases] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [downloadingItems, setDownloadingItems] = useState(new Set());

    useEffect(() => {
        if (isOpen) {
            loadTestCases();
        }
    }, [isOpen]);

    const loadTestCases = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await api.chat.listTestCases();
            setTestcases(data.testcases || []);
        } catch (err) {
            console.error('Error loading test cases:', err);
            setError(err.message);
            setTestcases([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async (testcaseName) => {
        setDownloadingItems(prev => new Set(prev).add(testcaseName));
        try {
            await api.chat.downloadTestCase(testcaseName);
        } catch (err) {
            console.error('Error downloading test case:', err);
            setError(err.message);
        } finally {
            setDownloadingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(testcaseName);
                return newSet;
            });
        }
    };

    const handleClose = () => {
        setError(null);
        onClose();
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const getFileIcon = (testcase) => {
        if (testcase.type === 'directory') {
            return <IoFolder className="w-5 h-5" />;
        }
        return <IoDocument className="w-5 h-5" />;
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={handleClose}
        >
            <div 
                className="flex flex-col bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh]"
                style={{ backgroundColor: 'var(--bg-primary)' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div className="flex justify-between items-center p-4 border-b" style={{ borderBottomColor: 'var(--border-color)' }}>
                    <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                        Test Cases
                    </h3>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        style={{ color: 'var(--text-primary)' }}
                    >
                        <IoClose className="w-6 h-6" />
                    </button>
                </div>

                {/* Modal Content */}
                <div className="flex-1 overflow-hidden p-6">
                    {isLoading ? (
                        <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
                            Loading test cases...
                        </div>
                    ) : error ? (
                        <div className="flex-1 flex items-center justify-center text-red-500">
                            {error}
                        </div>
                    ) : testcases.length === 0 ? (
                        <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
                            No test cases found
                        </div>
                    ) : (
                        <div className="overflow-y-auto h-full">
                            <div className="grid gap-4">
                                {testcases.map((testcase) => (
                                    <div
                                        key={testcase.name}
                                        className="flex items-center justify-between p-4 rounded-lg border hover:shadow-md transition-all"
                                        style={{
                                            backgroundColor: 'var(--bg-secondary)',
                                            borderColor: 'var(--border-color)'
                                        }}
                                    >
                                        <div className="flex items-center gap-4 flex-1">
                                            <div style={{ color: 'var(--blue-primary)' }}>
                                                {getFileIcon(testcase)}
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                                                    {testcase.name}
                                                </div>
                                                <div className="text-xs mt-1 flex gap-4" style={{ color: 'var(--text-secondary)' }}>
                                                    <span>
                                                        {testcase.type === 'directory' 
                                                            ? `${testcase.file_count} files` 
                                                            : formatFileSize(testcase.size)}
                                                    </span>
                                                    <span>
                                                        Modified: {new Date(testcase.modified).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleDownload(testcase.name)}
                                            disabled={downloadingItems.has(testcase.name)}
                                            className="ml-4 px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2"
                                            style={{
                                                backgroundColor: downloadingItems.has(testcase.name) 
                                                    ? 'var(--border-color)' 
                                                    : 'var(--blue-primary)',
                                                color: 'white',
                                                opacity: downloadingItems.has(testcase.name) ? 0.6 : 1,
                                                cursor: downloadingItems.has(testcase.name) ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            <IoDownload className="w-4 h-4" />
                                            {downloadingItems.has(testcase.name) ? 'Downloading...' : 'Download'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TestCases;
